import { useState } from 'react';
import { Text, Flex, Box, Button, Input } from '@opendoor/bricks/core';
import { Modal } from '@opendoor/bricks/complex';
import { Toast, showToast } from '@opendoor/bricks/complex/Toast';
import { PrivacyPolicy } from '@opendoor/cinderblocks/platform/PrivacyPolicy';

import { PrivacyPolicyDescription } from 'shared/components/PrivacyPolicyDescription';
import { useSetSMSReminder } from 'sellDirect/graphql/useSetSSVAReminder';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { getLeadInfo } from 'sellDirect/helpers/useCalendlyParams';
import { checkTelValidity } from '@opendoor/bricks/core/Input/helpers';

const TEXT_REMINDER_MODAL_LABEL = 'text-reminder-modal';

export interface ITextReminderCardProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

/**
 * Display a modal form for the user to input their phone number
 * a user can opt-in to scheduled reminders by inputting their phone number
 * load in a phone number if one already exists for a user otherwise we collect the user's number here
 */
export const TextReminderCard: React.FC<ITextReminderCardProps> = ({ onRequestClose, isOpen }) => {
  //check if the user has a number in our context and pre-populate the input field if possible
  const { lead } = useQualificationContext();
  const { phone } = getLeadInfo(lead);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(phone);
  const confirmDisabled = !phoneNumber || !checkTelValidity(phoneNumber);
  const { setSMSReminder } = useSetSMSReminder();
  const toastContent =
    "Thanks for signing up for a text reminder. We'll remind you to finish your walkthrough later today.";
  //triggers the mutation, takes a phone number and displays a toast on the page if the mutations returns as successful and closes the modal
  const handleReminderSubmission = async (phoneNumber: string) => {
    const res = await setSMSReminder(phoneNumber);
    if (res) {
      if (res.data?.sellDirect?.setSSVAReminderSMS?.success) {
        onRequestClose();
        showToast('info', toastContent, 'cake-ssva-text-reminder-signup-info-toast');
      }
    }
  };
  return (
    <>
      <Toast />
      <Modal
        analyticsName="cake-ssva-text-reminder-modal"
        isOpen={isOpen}
        showClose
        onRequestClose={onRequestClose}
        aria-labelledby={TEXT_REMINDER_MODAL_LABEL}
      >
        <Flex alignItems="flex-start" flexDirection="column">
          <Text
            id={TEXT_REMINDER_MODAL_LABEL}
            as="h2"
            fontSize="s3"
            width="100%"
            fontWeight="bold"
            lineHeight="s3"
            alignSelf={['center', 'center', 'center', 'start']}
          >
            Get a text reminder to do your walkthrough
          </Text>
          <Text size="s0" my={16}>
            We'll text you a reminder so you don't forget to complete your self-guided walkthrough.
          </Text>
          <Box width="100%">
            <Input
              analyticsName="cake-text-reminder-card-phone-number"
              data-testid="ssva-text-reminder-input"
              placeholder="555-555-5555"
              value={phoneNumber}
              id="input-tel-reminder"
              onChange={(_event, value) => {
                setPhoneNumber(value);
              }}
              label="Phone"
              type="tel"
              unboundedWidth
              autoComplete="tel"
            />
          </Box>
          <Box mt={8}>
            <PrivacyPolicy />
            <PrivacyPolicyDescription mt={5} ctaText="Remind Me" />
          </Box>
          <Button
            aria-label="Submit phone number for SMS reminders to complete your self-guided walkthrough."
            data-testid="ssva-text-submit"
            my={8}
            analyticsName="cake-ssva-subscribe-to-text-reminders"
            disabled={confirmDisabled}
            onClick={() => {
              if (phoneNumber) {
                handleReminderSubmission(phoneNumber);
              }
            }}
          >
            Remind Me
          </Button>
        </Flex>
      </Modal>
    </>
  );
};
